import { FC, memo, ReactNode } from "react";
import { styled } from "@mui/material";
import backdrop from "../../assets/landing/publications-bg.jpg";
import { publications } from "../../utils/landing-data";
import { bg, bp, fluid, font, w } from "../../styles/landing-tokens";
import { Container, DisplayHeading } from "./shared";
import { ArrowUpRight } from "./icons";
import { useReveal } from "./reveal";

type Publication = (typeof publications)[number];

/**
 * The heading sticks to the top of the viewport while the taller list of
 * publications scrolls past it, releasing at the end of the grid. Sticky is
 * dropped once the grid collapses to a single column (bp.md), where the heading
 * stacks above the list and pinning would cover the content.
 */
const StickyHeading = styled(DisplayHeading)({
  position: "sticky",
  top: 120,
  alignSelf: "start",

  [bp.md]: {
    position: "static",
    top: "auto",
  },
});

/** Section heading that fades and rises into view, then sticks while scrolling. */
const RevealHeading: FC<{ children: ReactNode }> = ({ children }) => {
  const { ref, style } = useReveal();
  return (
    <StickyHeading ref={ref} style={style}>
      {children}
    </StickyHeading>
  );
};

/** One publication row that fades and rises into view, staggered by index. */
const PublicationRow: FC<{ item: Publication; index: number }> = ({ item, index }) => {
  const { ref, style } = useReveal({ delay: index * 80 });

  return (
    <Row ref={ref} style={style} href={item.link} target="_blank" rel="noreferrer">
      <Year>{item.year}</Year>
      <Title className="title">{item.title}</Title>
      <Source>{item.source}</Source>
      <ArrowUpRight className="arrow" size={32} />
    </Row>
  );
};

const Section = styled("section")({
  position: "relative",
  background: bg,
  /** Figma: previous section ends at y=2624, this heading sits at y=3534. */
  paddingTop: 910,
  paddingBottom: 0,

  [bp.md]: { paddingTop: 300, paddingBottom: 0 },
  [bp.sm]: { paddingTop: 200 },
});

/**
 * Figma: RECTANGLE "image 1965" 1920×1064 @ y=2818, with a vertical
 * #060B14 gradient (transparent at 15% → opaque at 84%) laid over it.
 * It sits behind the heading and the first publication rows.
 */
const Backdrop = styled("div")({
  position: "absolute",
  /** Figma: image at y=2818, i.e. 194px into this section. */
  top: 194,
  left: 0,
  right: 0,
  height: 1064,
  backgroundImage: `linear-gradient(180deg, rgba(6,11,20,0) 15%, #060B14 84%), url(${backdrop})`,
  backgroundSize: "cover",
  backgroundPosition: "center top",
  pointerEvents: "none",

  [bp.md]: { height: 700 },
});

/**
 * Figma content 1488 wide: x 216 → 1704.
 *
 * This carries the #publications anchor rather than the section: the section
 * opens with 910px of padding and the backdrop photo, so scrolling to its top
 * left the heading below the fold. scroll-margin matches the sticky heading's
 * top so the heading lands exactly where it pins.
 */
const Inner = styled(Container)({
  position: "relative",
  zIndex: 1,
  maxWidth: 1568,
  scrollMarginTop: 120,

  [bp.md]: { scrollMarginTop: 80 },
});

/** Heading 524px on the left, list 808px on the right — Figma 216 / 896. */
const Grid = styled("div")({
  display: "grid",
  gridTemplateColumns: "524px 808px",
  columnGap: 156,
  alignItems: "start",

  /** Figma: heading at y=3534, first row 19px lower at y=3553. */
  "& > div:last-of-type": { marginTop: 19 },

  [bp.lg]: { gridTemplateColumns: "1fr 1.3fr", columnGap: 60 },
  [bp.md]: { gridTemplateColumns: "1fr", rowGap: 48 },
});

const List = styled("div")({
  display: "flex",
  flexDirection: "column",
});

/**
 * One publication: year, quoted title, journal, and a trailing ↗ icon.
 * Figma rows are ~243px apart; the icon is pinned to the top-right at x=1672.
 */
const Row = styled("a")({
  position: "relative",
  display: "block",
  /** Figma: text column 760 wide inside an 808 row, arrow pinned right. */
  paddingRight: 48,
  paddingBottom: 85,
  textDecoration: "none",
  cursor: "pointer",

  /** Figma: the list ends flush with the last row at y=5698. */
  "&:last-of-type": { paddingBottom: 0 },

  /** Figma: row group at y=3553, "Icon" 32×32 at (1672,3567) — 14px lower, flush right. */
  "& .arrow": {
    position: "absolute",
    top: 14,
    right: 0,
    transition: "transform .2s ease",
  },
  "&:hover .arrow": {
    transform: "translate(3px, -3px)",
  },
  "&:hover .title": {
    color: "#FFFFFF",
  },

  [bp.sm]: { paddingRight: 44, paddingBottom: 52 },
});

const Year = styled("div")({
  fontFamily: font.sans,
  fontWeight: 600,
  fontSize: 13.8,
  lineHeight: "20.7px",
  letterSpacing: "0.09em",
  color: "#FFFFFF",
});

const Title = styled("div")({
  marginTop: 14,
  fontFamily: font.sans,
  fontWeight: 500,
  fontSize: fluid(17, 23.8),
  lineHeight: 1.85,
  textAlign: "justify",
  color: w(0.95),
  transition: "color .2s ease",
});

const Source = styled("div")({
  /** Figma: title ends at y=3721, source starts at y=3735. */
  marginTop: 14,
  fontFamily: font.sans,
  fontWeight: 400,
  fontSize: 15,
  lineHeight: "22.6px",
  fontStyle: "italic",
  color: w(0.35),
});

export const Publications: FC = memo(() => (
  <Section>
    <Backdrop />

    <Inner id="publications">
      <Grid>
        <RevealHeading>
          {"Academic\npublications:\npeer-reviewed\nresearch."}
        </RevealHeading>

        <List>
          {publications.map((p, i) => (
            <PublicationRow key={`${p.year}-${i}`} item={p} index={i} />
          ))}
        </List>
      </Grid>
    </Inner>
  </Section>
));
